<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import ModalTabContent from "@/components/modal/ModalTabContent.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import TabsBootstrap from "@/components/TabsBootstrap.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSelect from "@/components/ArgonSelect.vue";
import ArgonCheckbox from "@/components/ArgonCheckbox.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import ArgonAddressNormalize from "@/components/ArgonAddressNormalize.vue";
import RouteColorInput from "@/views/vehicles/components/RouteColorInput.vue";
import { vehicleStore } from "@/_store/vehicleStore";
import { zoneStore } from "@/_store/zoneStore";
import { warehouseStore } from "@/_store/warehouseStore";
import { errorUtils } from "@/utils";
import { normalizeHexColor } from "@/utils/formatters";

const props = defineProps({
  vehicleId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(["close", "success"]);

const store = useStore();
const vehiclesStore = vehicleStore();
const zonesStore = zoneStore();
const warehousesStore = warehouseStore();
const bootstrap = store.state.bootstrap;
const modalRef = ref(null);
const snackbar = ref(null);
const zoneOptions = ref([]);
const warehouseOptions = ref([]);
const disableButton = ref(false);

const workingDaysOptions = [
  { value: "Monday", label: "Lunes" },
  { value: "Tuesday", label: "Martes" },
  { value: "Wednesday", label: "Miércoles" },
  { value: "Thursday", label: "Jueves" },
  { value: "Friday", label: "Viernes" },
  { value: "Saturday", label: "Sábado" },
  { value: "Sunday", label: "Domingo" },
];

const tabs = [
  { label: "Características", value: "features" },
  { label: "Horario", value: "schedule" },
  { label: "Restricciones", value: "restrictions" },
];
const tabContentHeight = 180;

const currentTab = ref("features");
const tabErrorFields = {
  features: ["registration_number"],
  schedule: ["working_days", "time_window.start_time", "time_window.end_time"],
  restrictions: ["zones"],
};

function createEmptyAddress() {
  return {
    address: "",
    label: "",
    coordenates: null,
    normalized: false,
    postal_code: "",
  };
}

function parseAddress(value) {
  if (!value || typeof value !== "object") return createEmptyAddress();
  return {
    address: value.address || "",
    label: value.label || "",
    coordenates: value.coordenates || null,
    normalized: !!value.normalized,
    postal_code: value.postal_code || "",
  };
}

const createDefaultForm = () => ({
  account_id: "",
  name: "",
  route_color_hex: "",
  model: "",
  registration_number: "",
  trailer_registration_number: "",
  special_authorization_number: "",
  max_weight: "",
  max_volume: "",
  stop_time: 4,
  warehouse_id: "",
  returns_to_origin: true,
  end_location: createEmptyAddress(),
  working_days: [],
  time_window: {
    start_time: "",
    end_time: "",
  },
  zones: [],
  active: true,
  deleted: false,
});

const form = ref(createDefaultForm());
const currentVehicle = ref(null);

const isEdit = computed(() => !!props.vehicleId);
const modalTitle = computed(() => (isEdit.value ? "Modificar" : "Nuevo"));
const rules = {
  name: {
    required: helpers.withMessage("Alias es requerido", required),
  },
  route_color_hex: {
    required: helpers.withMessage("Color de ruta es requerido", required),
    validHex: helpers.withMessage(
      "Color de ruta no es válido",
      (value) =>
        !String(value || "").trim() ||
        /^#[0-9A-Fa-f]{6}$/.test(String(value || ""))
    ),
  },
  warehouse_id: {
    required: helpers.withMessage("El punto habitual de origen es requerido", required),
  },
  end_location: {
    address: {
      required: helpers.withMessage(
        "La dirección de fin de ruta es requerida",
        requiredIf(() => !form.value.returns_to_origin)
      ),
    },
    normalized: {
      validNormalized: helpers.withMessage(
        "Debes validar la dirección de fin de ruta",
        (value) => form.value.returns_to_origin || !!value
      ),
    },
  },
  registration_number: {
    required: helpers.withMessage("Matrícula es requerida", required),
  },
  working_days: {
    required: helpers.withMessage("Días laborables es requerido", required),
  },
  time_window: {
    start_time: {
      required: helpers.withMessage("Hora inicio es requerida", required),
    },
    end_time: {
      required: helpers.withMessage("Hora fin es requerida", required),
    },
  },
  zones: {
    required: helpers.withMessage("Zona es requerida", required),
  },
};

const v$ = useVuelidate(rules, form);

const closeSnackbar = () => {
  snackbar.value = null;
};

function normalizeWorkingDay(day) {
  const mapping = {
    0: "Monday",
    1: "Tuesday",
    2: "Wednesday",
    3: "Thursday",
    4: "Friday",
    5: "Saturday",
    6: "Sunday",
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
  };

  const key = String(day || "")
    .trim()
    .toLowerCase();
  return mapping[key] ?? null;
}

function parseZoneIds(vehicle) {
  if (Array.isArray(vehicle?.zones) && vehicle.zones.length > 0) {
    return vehicle.zones
      .map((zone) => (typeof zone === "object" ? zone?._id || zone?.id : zone))
      .map((zone) => String(zone || "").trim())
      .filter(Boolean);
  }
  if (vehicle?.zone_id) {
    return [String(vehicle.zone_id).trim()].filter(Boolean);
  }
  return [];
}

function fillForm(vehicle) {
  const zoneIds = parseZoneIds(vehicle);
  const stopTimeValue = vehicle ? Number.parseInt(vehicle?.stop_time, 10) : 4;

  form.value = {
    account_id: vehicle?.account_id || "",
    name: vehicle?.name || "",
    route_color_hex: normalizeHexColor(vehicle?.route_color_hex),
    model: vehicle?.model || "",
    registration_number: vehicle?.registration_number || "",
    trailer_registration_number: vehicle?.trailer_registration_number || "",
    special_authorization_number: vehicle?.special_authorization_number || "",
    max_weight: vehicle?.max_weight ?? "",
    max_volume: vehicle?.max_volume ?? "",
    stop_time: Number.isFinite(stopTimeValue) ? stopTimeValue : 4,
    warehouse_id: vehicle?.warehouse_id || "",
    returns_to_origin: vehicle?.returns_to_origin ?? true,
    end_location: parseAddress(vehicle?.end_location),
    working_days: Array.isArray(vehicle?.working_days)
      ? vehicle.working_days
          .map((day) => normalizeWorkingDay(day))
          .filter((day) => day !== null)
      : [],
    time_window: {
      start_time:
        vehicle?.time_window?.start_time ||
        vehicle?.time_window?.from_time ||
        "",
      end_time:
        vehicle?.time_window?.end_time || vehicle?.time_window?.to_time || "",
    },
    zones: zoneIds,
    active: vehicle?.active ?? true,
    deleted: vehicle?.deleted ?? false,
  };

  currentTab.value = "features";
  v$.value.$reset();
}

watch(
  () => props.vehicleId,
  (value) => {
    disableButton.value = false;
    currentVehicle.value = null;
    fillForm(null);
    if (value) {
      vehiclesStore.getVehicleById(value);
    }
  },
  { immediate: true }
);

watch(
  () => vehiclesStore.vehicle,
  (value) => {
    if (!props.vehicleId || !value) return;
    if (String(value._id || "") !== String(props.vehicleId)) return;
    currentVehicle.value = value;
    fillForm(value);
  }
);

watch(
  () => vehiclesStore.vehicleCreated,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    modalRef.value.closeModal();
    emit("success");
  }
);

watch(
  () => vehiclesStore.vehicleUpdated,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    modalRef.value.closeModal();
    emit("success");
  }
);

watch(
  () => vehiclesStore.error,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);

function adjustZoneDropdownPosition() {
  const select = document.getElementById("vehicle-zone");
  const wrapper = select?.closest(".choices");
  const dropdown = wrapper?.querySelector(".choices__list--dropdown");
  if (!wrapper || !dropdown) return;

  const rect = wrapper.getBoundingClientRect();
  dropdown.style.position = "fixed";
  dropdown.style.top = `${rect.bottom}px`;
  dropdown.style.left = `${rect.left}px`;
  dropdown.style.width = `${rect.width}px`;
}

function onZoneDropdownShow() {
  adjustZoneDropdownPosition();
  window.addEventListener("scroll", adjustZoneDropdownPosition, true);
  window.addEventListener("resize", adjustZoneDropdownPosition);
}

function onZoneDropdownHide() {
  window.removeEventListener("scroll", adjustZoneDropdownPosition, true);
  window.removeEventListener("resize", adjustZoneDropdownPosition);
}

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById("modalVehicle"), {
    keyboard: true,
    backdrop: "static",
  });
  modal.show();
  zonesStore.getZones({ page: 1, limit: 500 }, {}, { _id: -1 });
  warehousesStore.getWarehouses({ page: 1, limit: 500 }, {}, { _id: -1 });

  const zoneSelect = document.getElementById("vehicle-zone");
  zoneSelect?.addEventListener("showDropdown", onZoneDropdownShow);
  zoneSelect?.addEventListener("hideDropdown", onZoneDropdownHide);
});

onBeforeUnmount(() => {
  const zoneSelect = document.getElementById("vehicle-zone");
  zoneSelect?.removeEventListener("showDropdown", onZoneDropdownShow);
  zoneSelect?.removeEventListener("hideDropdown", onZoneDropdownHide);
  onZoneDropdownHide();
});

function withZoneChoiceLabel(zones) {
  return zones.map((zone) => ({
    ...zone,
    zone_choice_label: `${zone.name} (${zone.abbreviation})`,
  }));
}

watch(
  () => zonesStore.zones,
  (value) => {
    if (Array.isArray(value?.data)) {
      zoneOptions.value = withZoneChoiceLabel(value.data);
      return;
    }

    if (Array.isArray(value?.zones)) {
      zoneOptions.value = withZoneChoiceLabel(value.zones);
      return;
    }

    if (Array.isArray(value)) {
      zoneOptions.value = withZoneChoiceLabel(value);
      return;
    }

    zoneOptions.value = [];
  }
);

watch(
  () => warehousesStore.warehouses,
  (value) => {
    const warehouses = Array.isArray(value?.data) ? value.data : [];
    warehouseOptions.value = warehouses
      .filter((warehouse) => warehouse?.type === "PROPIO")
      .sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
          sensitivity: "base",
        })
      );
  }
);

function closeModal() {
  emit("close");
}

async function validate() {
  await v$.value.$validate();
  return v$.value.$errors;
}

function getFirstTabWithErrors(errors) {
  const errorProperties = new Set(
    errors.map((error) =>
      String(error?.$propertyPath || error?.$property || "")
    )
  );

  return (
    tabs.find((tab) =>
      (tabErrorFields[tab.value] || []).some((field) =>
        errorProperties.has(field)
      )
    )?.value || null
  );
}

async function saveVehicle() {
  const errors = await validate();

  if (errors.length > 0) {
    const firstTabWithErrors = getFirstTabWithErrors(errors);
    if (firstTabWithErrors) {
      currentTab.value = firstTabWithErrors;
    }

    let message = "";
    for (const error of errors) {
      if (!message.includes(`<li>${error.$message}</li>`)) {
        message = `${message}<li>${error.$message}</li>`;
      }
    }

    snackbar.value = {
      title: "Error",
      message: `<ul>${message}</ul>`,
      type: "danger",
      color: "white",
    };
    return;
  }


  disableButton.value = true;
  const stopTimeValue = Number.parseInt(form.value.stop_time, 10);
  const request = {
    name: String(form.value.name || "").trim(),
    model: String(form.value.model || "").trim(),
    route_color_hex: String(form.value.route_color_hex || "").trim(),
    registration_number: String(form.value.registration_number || "").trim(),
    trailer_registration_number: String(
      form.value.trailer_registration_number || ""
    ).trim(),
    special_authorization_number: String(
      form.value.special_authorization_number || ""
    ).trim(),
    max_weight: Number.parseFloat(form.value.max_weight) || 0,
    max_volume: Number.parseFloat(form.value.max_volume) || 0,
    stop_time: Number.isFinite(stopTimeValue) ? stopTimeValue : 0,
    warehouse_id: String(form.value.warehouse_id || "").trim(),
    returns_to_origin: !!form.value.returns_to_origin,
    end_location: form.value.returns_to_origin
      ? null
      : {
          address: String(form.value.end_location.address || "").trim(),
          label: String(form.value.end_location.label || "").trim(),
          coordenates: form.value.end_location.coordenates || null,
          normalized: !!form.value.end_location.normalized,
          postal_code: String(form.value.end_location.postal_code || "").trim(),
        },
    working_days: Array.isArray(form.value.working_days)
      ? [...form.value.working_days]
      : [],
    time_window: {
      start_time:
        form.value.time_window?.start_time ||
        form.value.time_window?.from_time ||
        "",
      end_time:
        form.value.time_window?.end_time ||
        form.value.time_window?.to_time ||
        "",
    },
    zones: Array.isArray(form.value.zones)
      ? form.value.zones
          .map((zone) => String(zone || "").trim())
          .filter(Boolean)
      : [],
    notes: "",
    active: !!form.value.active,
  };
  if (isEdit.value) {
    vehiclesStore.updateVehicle(request, props.vehicleId);
    return;
  }
  vehiclesStore.createVehicle(request);
}
</script>

<template>
  <modal-base
    id="modalVehicle"
    ref="modalRef"
    width="40rem"
    min-height="30rem"
    @hide="closeModal"
  >
    <template #header>
      <h6 id="ModalLabel" class="modal-title">{{ modalTitle }}</h6>
    </template>

    <template #body>
      <div class="modal-form--wide-labels">
      <alert-message
        title="Aviso"
        type="info"
        class="mb-3"
      >
        Las modificaciones del vehículo no tendrán efecto en las planificaciones en curso.
      </alert-message>
      <div v-if="isEdit" class="row modal-form-row">
        <div class="modal-form-label">
          <label 
            for="vehicle-active"
            class="col-form-label"
          >
            Activado
          </label>
        </div>
        <div class="col">
          <div class="d-flex align-items-center h-100">
            <argon-checkbox
              id="vehicle-active"
              v-model="form.active"
              name="vehicle-active"
              class="mb-0"
            />
          </div>
        </div>
      </div>
      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label
            v-tooltip="'Alias y color con los que se identificará visualmente este vehículo y su ruta en la planificación.'"
            for="vehicle-name"
            class="col-form-label required"
          >
            Alias <sup class="text-primary sup-question">?</sup>
          </label>
        </div>
        <div class="col">
          <div class="vehicle-name-row">
            <route-color-input
              id="vehicle-route-color"
              v-model="form.route_color_hex"
              :error="v$.route_color_hex.$error"
              @update:model-value="v$.route_color_hex.$touch()"
              @blur="v$.route_color_hex.$touch()"
            />
            <argon-input
              id="vehicle-name"
              v-model="form.name"
              class="flex-grow-1"
              :error="v$.name.$error"
              :is-required="true"
              :max-length="18"
            />
          </div>
        </div>
      </div>
      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label
            v-tooltip="'Almacén propio desde el que sale el vehículo para iniciar su ruta. Se elige entre tus puntos habituales de tipo Propio.'"
            for="vehicle-warehouse"
            class="col-form-label required"
            >Origen<sup class="text-primary sup-question">?</sup></label
          >
        </div>
        <div class="col">
          <argon-select
            id="vehicle-warehouse"
            v-model="form.warehouse_id"
            name-value="_id"
            name-label="name"
            :options="warehouseOptions"
            :error="v$.warehouse_id.$error"
            :is-required="true"
            filterable
          />
        </div>
      </div>
      <div class="row modal-form-row">
        <div class="modal-form-label">
          <label
            v-tooltip="'Se usa para optimizar el orden de las paradas y calcular el tiempo de la ruta. Márcalo si el vehículo termina en su origen; si no, indica dónde termina.'"
            for="vehicle-returns-to-origin"
            class="col-form-label"
            >Vuelve al origen<sup class="text-primary sup-question">?</sup></label
          >
        </div>
        <div class="col">
          <div class="d-flex align-items-center h-100">
            <argon-checkbox
              id="vehicle-returns-to-origin"
              v-model="form.returns_to_origin"
              name="vehicle-returns-to-origin"
              class="mb-0"
            />
          </div>
        </div>
      </div>
      <div v-if="!form.returns_to_origin" class="row modal-form-row">
        <div class="modal-form-label">
          <label for="vehicle-end-location" class="col-form-label required"
            >Fin de ruta</label
          >
        </div>
        <div class="col">
          <argon-address-normalize
            id="vehicle-end-location"
            v-model="form.end_location"
            :error="
              v$.end_location.address.$error || v$.end_location.normalized.$error
            "
            label-input-id="vehicle-end-location-label"
            :label-max-length="180"
          />
        </div>
      </div>

      <tabs-bootstrap
        v-model="currentTab"
        :tabs="tabs"
        size="sm"
        refined
        style-nav="nav-underline" 
      />

      <modal-tab-content :height="`${tabContentHeight}px`">
      <div v-show="currentTab === 'features'">
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="vehicle-model" class="col-form-label">Modelo</label>
          </div>
          <div class="col">
            <argon-input
              id="vehicle-model"
              v-model="form.model"
              :max-length="25"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="vehicle-registration" class="col-form-label required"
              >Matrícula</label
            >
          </div>
          <div class="col">
            <argon-input
              id="vehicle-registration"
              v-model="form.registration_number"
              :max-length="25"
              :error="v$.registration_number.$error"
              :is-required="true"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label
              v-tooltip="'Matrícula del remolque o semirremolque, cuando el vehículo lleve uno.'"
              for="vehicle-trailer-registration"
              class="col-form-label"
              >Matrícula remolque <sup class="text-primary sup-question">?</sup></label
            >
          </div>
          <div class="col">
            <argon-input
              id="vehicle-trailer-registration"
              v-model="form.trailer_registration_number"
              :max-length="25"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label
              v-tooltip="'Nº de autorización especial de circulación del vehículo, cuando aplique.'"
              for="vehicle-special-authorization"
              class="col-form-label"
              >Autorización especial <sup class="text-primary sup-question">?</sup></label
            >
          </div>
          <div class="col">
            <argon-input
              id="vehicle-special-authorization"
              v-model="form.special_authorization_number"
              :max-length="40"
            />
          </div>
        </div>

      </div>

      <div v-show="currentTab === 'schedule'">
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label v-tooltip="'Define la franja horaria en la que el vehículo puede operar.'" for="vehicle-start-time" class="col-form-label required"
              >Horario <sup class="text-primary sup-question">?</sup></label
            >
          </div>
          <div class="col">
            <div class="row g-2">
              <argon-input
                id="vehicle-start-time"
                v-model="form.time_window.start_time"
                type="time"
                class="col-lg-6 col-md-6 col-sm-12"
                :error="v$.time_window.start_time.$error"
                :is-required="true"
              />
              <argon-input
                id="vehicle-end-time"
                v-model="form.time_window.end_time"
                type="time"
                class="col-lg-6 col-md-6 col-sm-12"
                :error="v$.time_window.end_time.$error"
                :is-required="true"
              />
            </div>
          </div>
        </div>
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label v-tooltip="'Indican los dias en los que este vehiculo esta disponible.'" for="vehicle-working-days" class="col-form-label required"
              >Días laborables <sup class="text-primary sup-question">?</sup></label
            >
          </div>
          <div class="col">
            <argon-select
              id="vehicle-working-days"
              v-model="form.working_days"
              name-label="label"
              name-value="value"
              :options="workingDaysOptions"
              :multiple="true"
              filterable
              :error="v$.working_days.$error"
            />
          </div>
        </div>
      </div>

      <div v-show="currentTab === 'restrictions'">
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label v-tooltip="'Sirven para limitar en que zonas puede trabajar el vehiculo.'" for="vehicle-zone" class="col-form-label required"
              >Zonas <sup class="text-primary sup-question">?</sup></label
            >
          </div>
          <div class="col">
            <argon-select
              id="vehicle-zone"
              v-model="form.zones"
              name-value="_id"
              name-label="abbreviation"
              choice-label="zone_choice_label"
              selected-tooltip
              selected-tooltip-field="name"
              :options="zoneOptions"
              :error="v$.zones.$error"
              :is-required="true"
              filterable
              :multiple="true"
              menu-height="150"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label
              v-tooltip="'Tiempo en minutos que tarda el mensajero en entregar o recoger cuando llega a la parada.'"
              for="vehicle-stop-time"
              class="col-form-label"
            >
              Tiempo parada <sup class="text-primary sup-question">?</sup>
            </label>
          </div>
          <div class="col">
            <argon-input
              id="vehicle-stop-time"
              v-model="form.stop_time"
              type="number"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="vehicle-max-weight" class="col-form-label"
              >Kg. máximos</label
            >
          </div>
          <div class="col">
            <argon-input
              id="vehicle-max-weight"
              v-model="form.max_weight"
              type="number"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="vehicle-max-volume" class="col-form-label"
              >m³ máximos</label
            >
          </div>
          <div class="col">
            <argon-input
              id="vehicle-max-volume"
              v-model="form.max_volume"
              type="number"
            />
          </div>
        </div>
      </div>
      </modal-tab-content>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="disableButton ? 'Guardando...' : 'Guardar'"
        :loading="disableButton"
        :disable-button="disableButton"
        @click="saveVehicle"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>

<style scoped>
.vehicle-name-row {
  align-items: flex-start;
  display: flex;
  gap: 0.65rem;
}

.vehicle-name-row :deep(.route-color-control) {
  margin-top: 0.1rem;
}

</style>
